import { useEffect, useState } from "react";
import { CalendarDays, Clock } from "lucide-react";
import { api } from "@/lib/api";

// Per-session cache so the four list pages don't each issue their own
// /api/settings/shift-cutoff request on mount (testing-agent code-review
// flag, iteration_22). Invalidate by reload — admin saves through the
// Settings panel are infrequent and tolerable to refresh manually.
let _cutoffCache = null;

/**
 * Today | History scope picker — drop-in component for every list view.
 *
 * Behaviour:
 *   • Default mode = "today" (no date params, parent should pass today:1)
 *   • Click "History" → swap to a two-input date range
 *   • Emits `{ today, from_date, to_date }` to parent via `onChange`
 *   • Reads `/api/settings/shift-cutoff` once so we can label the scope
 *     with the actual shift cutoff (e.g. "Today · 03:00–03:00")
 *
 * The parent component is responsible for translating the emitted scope
 * into list-API query params.
 */
export default function DateScopePicker({ value, onChange, className = "" }) {
  const [cutoff, setCutoff] = useState(3);
  const mode = value?.mode || "today";

  useEffect(() => {
    if (_cutoffCache !== null) {
      setCutoff(_cutoffCache);
      return;
    }
    api
      .get("/settings/shift-cutoff")
      .then((r) => {
        _cutoffCache = r.data?.hour ?? 3;
        setCutoff(_cutoffCache);
      })
      .catch(() => {});
  }, []);

  const isoToday = () => new Date().toISOString().slice(0, 10);

  const emit = (next) => onChange?.(next);

  return (
    <div
      className={`flex flex-wrap items-center gap-2 ${className}`}
      data-testid="date-scope-picker"
    >
      <div
        className="inline-flex h-10 rounded-lg border border-border overflow-hidden bg-stone-50 dark:bg-stone-800"
        role="tablist"
      >
        <button
          type="button"
          role="tab"
          aria-selected={mode === "today"}
          onClick={() => emit({ mode: "today" })}
          data-testid="scope-today"
          className={`px-3 h-10 text-sm font-medium inline-flex items-center gap-1.5 transition ${
            mode === "today"
              ? "bg-white dark:bg-stone-900 text-stone-900 dark:text-white"
              : "text-stone-600 hover:text-stone-900"
          }`}
        >
          <Clock className="h-4 w-4" /> Today
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "history"}
          onClick={() =>
            emit({
              mode: "history",
              from_date: value?.from_date || isoToday(),
              to_date: value?.to_date || isoToday(),
            })
          }
          data-testid="scope-history"
          className={`px-3 h-10 text-sm font-medium inline-flex items-center gap-1.5 transition ${
            mode === "history"
              ? "bg-white dark:bg-stone-900 text-stone-900 dark:text-white"
              : "text-stone-600 hover:text-stone-900"
          }`}
        >
          <CalendarDays className="h-4 w-4" /> History
        </button>
      </div>

      {mode === "history" && (
        <div className="flex items-center gap-2" data-testid="scope-range">
          <input
            type="date"
            value={value?.from_date || isoToday()}
            max={value?.to_date || undefined}
            onChange={(e) =>
              emit({ mode: "history", from_date: e.target.value, to_date: value?.to_date || isoToday() })
            }
            className="h-10 px-2 border border-border rounded-lg bg-white dark:bg-stone-900 text-sm"
            data-testid="scope-from-date"
          />
          <span className="text-stone-500 text-sm">→</span>
          <input
            type="date"
            value={value?.to_date || isoToday()}
            min={value?.from_date || undefined}
            max={isoToday()}
            onChange={(e) =>
              emit({ mode: "history", from_date: value?.from_date || isoToday(), to_date: e.target.value })
            }
            className="h-10 px-2 border border-border rounded-lg bg-white dark:bg-stone-900 text-sm"
            data-testid="scope-to-date"
          />
        </div>
      )}

      {mode === "today" && (
        <span className="text-xs text-stone-500 ml-1">
          Shift starts {String(cutoff).padStart(2, "0")}:00
        </span>
      )}
    </div>
  );
}

/**
 * Translate the scope value emitted by DateScopePicker into the
 * `{today, from_date, to_date}` axios params object every list endpoint
 * accepts. Returns null when there's nothing to send (i.e. just empty).
 */
export function scopeToParams(scope) {
  if (!scope || scope.mode === "today") return { today: 1 };
  if (scope.mode === "history") {
    const p = {};
    if (scope.from_date) p.from_date = scope.from_date;
    if (scope.to_date) p.to_date = scope.to_date;
    return p;
  }
  return {};
}

/**
 * Trigger a CSV download of an array of objects. Used by the History
 * sub-views on each page.
 */
export function downloadCsv(filename, rows, columns) {
  if (!rows?.length) return;
  const head = columns.map((c) => c.header).join(",");
  const body = rows
    .map((r) =>
      columns
        .map((c) => {
          const raw = typeof c.value === "function" ? c.value(r) : r[c.value];
          const s = raw === undefined || raw === null ? "" : String(raw);
          // CSV-safe + formula-injection neutralisation (SEC-005 from the
          // security audit): prefix risky leading chars with an apostrophe.
          const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
          return `"${safe.replace(/"/g, '""')}"`;
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob([head + "\n" + body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
